/**
 * 상품 지도 핀 압축 규칙 — **순수 함수**.
 *
 * 검색 지도의 마커는 `상품 × 지점`으로 만들어진다. dev 실측으로 승인 상품 252개가
 * 마커 4,427개가 되는데, 그중 4,158개는 **같은 좌표에 정확히 겹쳐** 있어 화면에는
 * 처음부터 맨 위 하나만 보였다. 보이지도 않는 핀이 매 프레임 O(n²) 거리 계산에
 * 들어가던 것이 "지도를 확대하면 렉이 걸리는" 증상의 직접 원인이다.
 *
 * 압축은 두 단계다.
 *   C1 `groupMarkersBySpot` — 같은 좌표를 핀 하나로 (4,427 → 269, 줌과 무관)
 *   C2 `productClusterRadiusKm` — 줌에 비례한 반경으로 스팟들을 다시 묶음
 *
 * C1은 핀의 **뜻**을 바꾸지 않는다. 핀은 여전히 "그 자리에서 빌릴 수 있는 상품들"을
 * 가리키고, 아이콘도 지점이 아니라 그 상품들의 카테고리를 말한다. dev 기준 좌표
 * 269곳 중 250곳은 카테고리가 한 종류라 아이콘 한 장으로 정확하고, 섞인 19곳만
 * 아이콘이 겹쳐 "여기는 하나가 아니다"를 표시한다.
 */

export interface SpotInput {
  id: string | number;
  location?: { lat: number; lng: number } | null;
  categoryCode?: string;
}

export interface Spot<T extends SpotInput = SpotInput> {
  /** 좌표 키 — 같은 자리를 가르는 식별자 */
  key: string;
  lat: number;
  lng: number;
  /** 이 자리에 있는 원본 마커들 (상품 단위) */
  markers: T[];
  /** 이 자리 상품들의 카테고리 — 첫 등장 순서, 중복 없음 */
  categoryCodes: string[];
}

/**
 * 좌표를 소수점 5자리(≈1.1m)로 끊어 키를 만든다.
 *
 * 지점 좌표는 같은 값이 그대로 들어오므로 반올림 없이도 대부분 묶이지만, 파트너가
 * 같은 주소를 소수점 끝자리만 다르게 올린 경우까지 같은 자리로 본다. 1m는 건물
 * 하나보다 훨씬 작아 서로 다른 지점을 잘못 합칠 위험이 없다.
 *
 * **선택된 핀의 식별자**로도 쓴다. 클러스터 id(`cluster-N`)는 줌·이동으로 다시
 * 묶일 때마다 번호가 바뀌어, 그걸로 선택을 기억하면 지도를 움직이는 순간 엉뚱한
 * 핀이 활성화된다. 좌표는 묶임과 무관하게 그대로다.
 */
export function spotKey(lat: number, lng: number): string {
  return `${lat.toFixed(5)},${lng.toFixed(5)}`;
}

/**
 * C1 — 같은 좌표의 마커를 하나의 스팟으로 묶는다.
 *
 * 좌표가 없거나 숫자가 아닌 마커는 버린다(지도에 그릴 수 없다). 스팟 순서는 입력
 * 순서를 따르므로, 정렬이 바뀌어도 핀이 무작위로 재배치되지 않는다.
 */
export function groupMarkersBySpot<T extends SpotInput>(markers: T[]): Spot<T>[] {
  const byKey = new Map<string, Spot<T>>();

  for (const marker of markers) {
    const lat = marker.location?.lat;
    const lng = marker.location?.lng;
    if (
      lat == null ||
      lng == null ||
      !Number.isFinite(lat) ||
      !Number.isFinite(lng)
    ) {
      continue;
    }

    const key = spotKey(lat, lng);
    let spot = byKey.get(key);
    if (!spot) {
      spot = { key, lat, lng, markers: [], categoryCodes: [] };
      byKey.set(key, spot);
    }

    spot.markers.push(marker);
    if (marker.categoryCode && !spot.categoryCodes.includes(marker.categoryCode)) {
      spot.categoryCodes.push(marker.categoryCode);
    }
  }

  return Array.from(byKey.values());
}

/**
 * 기준 반경(km) @ zoom 14.
 *
 * 지점 허브(`lib/mobility/station-cluster`)는 전국 643지점을 한 화면에 놓는 화면이라
 * 0.4km를 쓰지만, 상품 지도는 좌표 269곳이라 같은 값이면 도시 줌에서 과하게 뭉쳐
 * "상품이 없어 보인다". 절반으로 시작해 실화면을 보고 0.1~0.3 사이에서 조정한다.
 */
export const PRODUCT_CLUSTER_BASE_RADIUS_KM = 0.2;
const BASE_ZOOM = 14;

/** 같은 건물 안 좌표까지 갈라지지 않게 하는 하한 */
export const PRODUCT_CLUSTER_MIN_RADIUS_KM = 0.05;
/** 나라 하나가 한 덩어리로 뭉개지지 않게 하는 상한 */
export const PRODUCT_CLUSTER_MAX_RADIUS_KM = 150;

/**
 * C2 — 클러스터 반경(km) = `0.2 × 2^(14 − zoom)`, [0.05, 150] 클램프.
 *
 * 줌이 1 줄면 화면이 덮는 실제 거리가 2배가 되므로 반경도 2배가 되어야 "화면에서 본
 * 덩어리 크기"가 어느 줌에서나 일정하다. 기존 상수 계단(0.05 / 0.1 / 0.2km)은 나라
 * 단위 줌에서 사실상 묶이지 않아 도쿄 위에 핀 수백 개가 겹쳤다.
 */
export function productClusterRadiusKm(zoom: number): number {
  if (!Number.isFinite(zoom)) return PRODUCT_CLUSTER_BASE_RADIUS_KM;
  const raw = PRODUCT_CLUSTER_BASE_RADIUS_KM * Math.pow(2, BASE_ZOOM - zoom);
  return Math.min(
    PRODUCT_CLUSTER_MAX_RADIUS_KM,
    Math.max(PRODUCT_CLUSTER_MIN_RADIUS_KM, raw),
  );
}

/**
 * 마커 id(`상품id-지점순번`)에서 상품 id 만 떼어 낸다. 순번이 없으면 그대로.
 * 목록은 상품 단위라, 한 상품이 여러 지점에 걸쳐 있어도 한 번만 세어야 한다.
 */
export function baseProductId(id: string | number): string {
  const s = String(id);
  const lastDash = s.lastIndexOf("-");
  if (lastDash !== -1 && /^\d+$/.test(s.substring(lastDash + 1))) {
    return s.substring(0, lastDash);
  }
  return s;
}

export interface SpotScope {
  /** 범위 식별자 — 같은 자리들이면 누른 순서와 무관하게 같다 */
  id: string;
  /** 목록에 남길 상품 id (지점 접미사 없음) */
  productIds: string[];
  /** 이 범위가 덮는 자리들의 좌표 키 — 이 자리의 핀을 검정으로 반전시킨다 */
  spotKeys: string[];
}

/**
 * 핀 하나를 눌렀을 때의 목록 범위.
 *
 * 핀을 누르면 모달 없이 **그 핀에 묶인 상품 전부**를 목록에 남긴다(09-23 결정 —
 * 카테고리가 섞여도 묻지 않는다. 목록 카드가 이미 카테고리를 보여 준다). 여러 자리가
 * 묶인 핀은 지도가 그 자리들로 확대되며 갈라지는데, 갈라진 핀이 모두 선택으로 남도록
 * 자리마다 키를 담는다.
 */
export function spotScopeOf(markers: SpotInput[]): SpotScope {
  const spotKeys = new Set<string>();
  const productIds = new Set<string>();

  for (const marker of markers) {
    productIds.add(baseProductId(marker.id));
    const lat = marker.location?.lat;
    const lng = marker.location?.lng;
    if (lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)) {
      spotKeys.add(spotKey(lat, lng));
    }
  }

  const keys = Array.from(spotKeys);
  return {
    id: [...keys].sort().join("|"),
    productIds: Array.from(productIds),
    spotKeys: keys,
  };
}

/**
 * 반경이 더 줄지 않는 줌 — 이 줌부터는 반경이 하한(50m)에 머물러 더 확대해도
 * 새로 갈라지는 핀이 없다. `0.2 × 2^(14 − z) = 0.05` → z = 16.
 */
export const PRODUCT_CLUSTER_FLOOR_ZOOM =
  BASE_ZOOM +
  Math.log2(PRODUCT_CLUSTER_BASE_RADIUS_KM / PRODUCT_CLUSTER_MIN_RADIUS_KM);

/** 두 좌표 사이 거리(km) — 하버사인. MapView 의 거리 계산과 같은 식이다. */
export function haversineKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/**
 * 지도가 핀을 묶는 규칙 — **MapView 와 확대 계산이 같은 함수를 쓴다.**
 *
 * 순서대로 돌며 아직 안 묶인 첫 점을 기준점으로 삼고, **기준점에서 반경 안**인 나머지를
 * 한 덩어리로 묶는다(사슬로 잇지 않는다). 둘이 다른 규칙을 쓰면 "계산상 갈라지는 줌까지
 * 확대했는데 지도에선 안 갈라지는" 핀이 생긴다.
 *
 * @param skip 어느 덩어리에도 넣지 않을 점 — 지점 허브의 선택·펼친 지점
 * @returns 덩어리마다 점 번호 목록(입력 순서)
 */
export function clusterPoints(
  points: ReadonlyArray<{ lat: number; lng: number }>,
  radiusKm: number,
  skip?: ReadonlySet<number>,
): number[][] {
  const processed = new Set<number>(skip);
  const groups: number[][] = [];

  points.forEach((seed, index) => {
    if (processed.has(index)) return;
    const group = [index];
    processed.add(index);
    points.forEach((other, otherIndex) => {
      if (processed.has(otherIndex)) return;
      if (haversineKm(seed, other) <= radiusKm) {
        group.push(otherIndex);
        processed.add(otherIndex);
      }
    });
    groups.push(group);
  });

  return groups;
}

/**
 * 겹친 핀이 **처음 갈라지는 줌** — 누르면 이 줌까지만 확대한다.
 *
 * `fitBounds` 로 자리들을 화면에 꽉 채우면 한 번에 여러 단계를 뛰어넘어 "확 확대"
 * 된다. 갈라지는 줌까지만 가면 누를 때마다 한 겹씩 풀린다(09-23 사용자 요청).
 *
 * @param fromZoom 지금 지도가 클러스터링에 쓰는 줌(정수) — 이보다 큰 줌만 본다
 * @returns 갈라지는 줌. 반경 하한(50m)보다 가까이 붙어 끝까지 안 갈라지면 null
 */
export function expansionZoom(
  points: ReadonlyArray<{ lat: number; lng: number }>,
  fromZoom: number,
): number | null {
  for (
    let z = Math.floor(fromZoom) + 1;
    z <= PRODUCT_CLUSTER_FLOOR_ZOOM;
    z++
  ) {
    if (clusterPoints(points, productClusterRadiusKm(z)).length > 1) return z;
  }
  return null;
}
